import { useCallback } from 'react';
import type { Except } from 'type-fest';

import {
  getAllFilesFromNativeFileBundle,
  type NativeFileBundle,
} from 'components/customDragLayer';

import { useFilesAndDirectoriesUploadToasts } from './useFilesAndDirectoriesUploadToasts';

export type ExtractFilesFromNativeFileBundleForUploadParam = {
  nativeFileBundle: NativeFileBundle;
  onExtractFiles?: (files: File[]) => void;
  onExtractNoFiles?: () => void;
  onError?: (error: unknown) => void;
};

export type UseExtractFilesFromNativeFileBundleDuringUploadParam = Except<
  ExtractFilesFromNativeFileBundleForUploadParam,
  'nativeFileBundle'
>;

export const useExtractFilesFromNativeFileBundleForUpload = ({
  onExtractFiles: onExtractFilesOuter,
  onExtractNoFiles: onExtractNoFilesOuter,
  onError: onErrorOuter,
}: UseExtractFilesFromNativeFileBundleDuringUploadParam = {}) => {
  const { createUploadNoFilesToast, createUploadErrorToast } =
    useFilesAndDirectoriesUploadToasts();

  const defaultOnExtractNoFiles = useCallback(() => {
    createUploadNoFilesToast();
  }, [createUploadNoFilesToast]);

  const defaultOnError = useCallback(() => {
    createUploadErrorToast();
  }, [createUploadErrorToast]);

  const extractFilesFromNativeFileBundleForUpload = useCallback(
    async ({
      nativeFileBundle,
      onExtractFiles: onExtractFilesOverride,
      onExtractNoFiles: onExtractNoFilesOverride,
      onError: onErrorOverride,
    }: ExtractFilesFromNativeFileBundleForUploadParam) => {
      const onExtractFiles = onExtractFilesOverride ?? onExtractFilesOuter;

      const onExtractNoFiles =
        onExtractNoFilesOverride ??
        onExtractNoFilesOuter ??
        defaultOnExtractNoFiles;

      const onError = onErrorOverride ?? onErrorOuter ?? defaultOnError;

      try {
        const files = await getAllFilesFromNativeFileBundle(nativeFileBundle);

        if (files.length > 0) {
          onExtractFiles?.(files);
        } else {
          onExtractNoFiles();
        }
      } catch (error) {
        onError(error);
      }
    },
    [
      onExtractFilesOuter,
      onExtractNoFilesOuter,
      defaultOnExtractNoFiles,
      onErrorOuter,
      defaultOnError,
    ],
  );

  return extractFilesFromNativeFileBundleForUpload;
};
